import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, map, switchMap } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Invoice } from '../../models/invoice';
import { Campaign } from '../../models/campaign';
import { CampaignService } from '../../services/campaign.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { InvoiceService, toInvoiceAmount } from '../../services/invoice.service';
import { RosterService } from '../../services/roster.service';
import { Roster } from '../../models/roster';
import { CompanyService } from '../../services/company.service';

@Component({
  selector: 'app-invoices',
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.css'
})
export class InvoicesComponent implements OnInit, OnDestroy {
  private rateFilterSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  invoices: Invoice[] = [];
  filteredInvoices: Invoice[] = [];
  // Map from campaignId -> { rate, name, accountId } (values may be undefined until populated)
  campaignLookup: Record<string, { rate: number; name: string; accountId?: string } | undefined> = {};
  // Map from accountId -> account name
  accountLookup: Record<string, string> = {};
  // Map from rosterId -> name for quick creator name lookup
  rosterLookup: Record<string, string> = {};
  // Map from campaignId -> invoicing status ('' | 'Under' | 'Over' | 'Missing' | 'No Go Live')
  campaignStatusLookup: Record<string, '' | 'Under' | 'Over' | 'Missing' | 'No Go Live'> = {};
  // Keep a local copy of campaigns so we can detect campaigns without invoices
  campaigns: Campaign[] = [];

  // Filter properties
  selectedCreatorName: string = '';
  refFilterString: string = '';
  campaignNameFilter: string = '';
  rateFilter: string = '';
  accountFilter: string = '';
  activeStatusFilter: string | null = null;

  completedInvoiceAmount: number = 0;
  noInvoiceAmount: number = 0;
  overdueInvoiceAmount: number = 0;
  invoicedNotCollected: number = 0;
  creatorPaymentNeeded: number = 0;

  constructor(private campaignService: CampaignService, private invoiceService: InvoiceService, private rosterService: RosterService, private companyService: CompanyService) { }

  ngOnInit(): void {
    this.rateFilterSubject.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(() => this.applyFilter());

    this.companyService.getCompanies().subscribe({
      next: companies => {
        companies.forEach(c => { this.accountLookup[c.id] = c.name; });
      },
      error: err => console.log('Error loading companies:', err)
    });

    // Load roster data first to build the lookup table
    this.rosterService.getRosters().subscribe({
      next: rosters => {
        // Build roster lookup dictionary
        rosters.forEach(roster => {
          this.rosterLookup[roster.id] = roster.name;
        });

        // After roster lookup is built, load campaigns
        this.loadCampaigns();
      },
      error: err => {
        console.log('Error loading rosters:', err);
        // Still try to load campaigns even if rosters fail
        this.loadCampaigns();
      }
    });

    this.invoiceService.getInvoices().subscribe({
      next: result => {
        // make a shallow copy so we don't mutate the service cache when adding placeholders
        this.invoices = result ? result.slice() : [];
        // ensure invoices reflect campaigns (add placeholders for missing invoices)
        this.mergeCampaignsAndInvoices();
      },
      error: err => {
        console.log(err);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRateFilterChange(): void {
    this.rateFilterSubject.next(this.rateFilter);
  }

  filterByStatus(status: string): void {
    this.activeStatusFilter = this.activeStatusFilter === status ? null : status;
    this.applyFilter();
  }

  clearFilters(): void {
    this.selectedCreatorName = '';
    this.refFilterString = '';
    this.campaignNameFilter = '';
    this.rateFilter = '';
    this.accountFilter = '';
    this.activeStatusFilter = null;
    this.applyFilter();
  }

  private getInvoiceStatusKey(invoice: Invoice): string {
    if (invoice.invoiceNumber === 'no-invoice') return 'no-invoice';
    if (this.isOverdue(invoice.dueDate!) && !invoice.paidDate) return 'overdue';
    if (invoice.actualPayAmount && invoice.actualPayAmount > 0 && invoice.creatorPaidDate) return 'completed';
    if (invoice.actualPayAmount && invoice.actualPayAmount > 0 && !invoice.creatorPaidDate) return 'creator-payment';
    return 'invoiced-not-collected';
  }

  private loadCampaigns(): void {
    // Lean campaign list, plus only the "Go Live" tasks that computeCampaignStatus needs.
    this.campaignService.getCampaigns().pipe(
      switchMap(campaigns => this.campaignService
        .loadTasks({ descriptionContains: 'go live' })
        .pipe(map(() => campaigns)))
    ).subscribe({
      next: result => {
        this.campaigns = result;
        result.forEach(c => {
          // skip deleted campaigns
          if ((c as any).deleted) return;

          // populate lookup for quick access by campaign id
          try {
            this.campaignLookup[c.id] = { rate: c.rate || 0, name: c.name || '', accountId: c.accountId };

            // Update campaign's creatorName if we have roster data and creatorId
            if (c.creatorId && this.rosterLookup[c.creatorId]) {
              c.creatorName = this.rosterLookup[c.creatorId];
            }
          } catch (err) {
            // ignore malformed campaign
          }
        });

        // ensure invoices reflect campaigns (add placeholders for missing invoices)
        this.mergeCampaignsAndInvoices();
      },
      error: err => {
        console.log(err);
      }
    });
  }

  /**
   * Merge campaigns and invoices: for any campaign that has no invoice, insert a placeholder
   * invoice object with invoiceNumber='no-invoice' and issueDate set to campaign.createdDate.
   * Finally sort the invoices by issueDate ascending (oldest first).
   */
  mergeCampaignsAndInvoices(): void {
    if (!this.campaigns || this.campaigns.length === 0) return;

    // Build a set of campaignIds that already have invoices
    const invoiceCampaignIds = new Set(this.invoices.map(inv => inv.campaignId));

    // For each campaign missing an invoice, push a placeholder
    this.campaigns.forEach(c => {
      // skip deleted campaigns
      if ((c as any).deleted) return;

      if (!invoiceCampaignIds.has(c.id)) {
        const placeholder: Invoice = {
          id: 'no-invoice-' + c.id,
          campaignId: c.id,
          invoiceNumber: 'no-invoice',
          inRef: '',
          outRef: '',
          issueDate: c.createdDate || undefined,
          dueDate: undefined,
          paidDate: undefined,
          showWire: false,
          actualPayAmount: 0,
          creatorPaidDate: undefined,
          creatorPayAmount: 0,
          billTo: '',
          invoiceNotes: '',
          lineItems: [],
          statusNote: '',
          creatorFacingStatus: ''
        };

        this.invoices.push(placeholder);
      }
    });

    // Sort invoices by issueDate (newest first, undefined last)
    this.invoices.sort((a, b) => {
      const aDate = a.issueDate ? a.issueDate : '0000-01-01';
      const bDate = b.issueDate ? b.issueDate : '0000-01-01';
      if (aDate > bDate) return -1;
      if (aDate < bDate) return 1;
      return 0;
    });

    // Build the per-campaign invoicing status lookup now that campaigns and invoices are both loaded.
    this.buildCampaignStatusLookup();

    // Apply filtering
    this.applyFilter();
  }

  /**
   * Compute the invoicing status for every campaign and cache it by campaignId so the
   * template can read campaignStatusLookup[campaignId] without recomputing per change
   * detection cycle. Matches the campaignLookup / accountLookup pattern above.
   */
  private buildCampaignStatusLookup(): void {
    this.campaignStatusLookup = {};
    this.campaigns.forEach(c => {
      if ((c as any).deleted) return;
      this.campaignStatusLookup[c.id] = this.computeCampaignStatus(c);
    });
  }

  /**
   * Determine whether a campaign is Under / Over / Missing / No Go Live invoiced, or '' (fine).
   * See plan: campaigns with no "Go Live" tasks are flagged whenever they aren't fully
   * invoiced (Over still surfaces ahead of "No Go Live"); campaigns that have Go Live tasks
   * are only flagged once they "should be invoiced" (all Go Live tasks complete OR the
   * campaign is more than 3 months old).
   */
  private computeCampaignStatus(c: Campaign): '' | 'Under' | 'Over' | 'Missing' | 'No Go Live' {
    const state = this.invoiceState(c);

    const goLiveTasks = (c.tasks || []).filter(t => (t.description || '').toLowerCase().includes('go live'));
    const hasGoLive = goLiveTasks.length > 0;

    // Campaigns with NO Go Live tasks are not subject to the Go Live completion gate.
    if (!hasGoLive) {
      if (state === 'full') return '';
      if (state === 'over') return 'Over';
      return 'No Go Live';
    }

    // Campaigns WITH Go Live tasks: only flag when they "should be invoiced".
    const allGoLiveComplete = goLiveTasks.every(t => t.complete === true);
    const shouldBeInvoiced = allGoLiveComplete || this.createdMoreThan3MonthsAgo(c.createdDate);
    if (!shouldBeInvoiced) return '';
    if (state === 'none') return 'Missing';
    if (state === 'under') return 'Under';
    if (state === 'over') return 'Over';
    return '';
  }

  /**
   * Classify how fully a campaign is invoiced based on its real (non-placeholder) invoices.
   * Returns 'none' (no invoices), 'under', 'over', or 'full' (within a $100 rounding tolerance).
   */
  private invoiceState(c: Campaign): 'none' | 'under' | 'over' | 'full' {
    const realInvoices = this.invoices.filter(inv => inv.campaignId === c.id && inv.invoiceNumber !== 'no-invoice');
    if (realInvoices.length === 0) return 'none';

    // Creator-paid-commission scenario is marked by any CreatorPaidCommission line item.
    const isCreatorPaidCommission = realInvoices.some(inv =>
      (inv.lineItems || []).some(li => li.lineItemType === 'CreatorPaidCommission'));

    let target: number;
    let actual = 0;
    if (isCreatorPaidCommission) {
      target = (c.rate || 0) * (c.commission || 0) / 100;
      realInvoices.forEach(inv => (inv.lineItems || []).forEach(li => {
        if (li.lineItemType === 'CreatorPaidCommission') actual += (li.unitPrice || 0);
      }));
    } else {
      target = c.rate || 0;
      // Absent/'BasePayment' line items are the commissionable base payment; expenses are ignored.
      realInvoices.forEach(inv => (inv.lineItems || []).forEach(li => {
        if (!li.lineItemType || li.lineItemType === 'BasePayment') actual += (li.unitPrice || 0);
      }));
    }

    if (actual < target - 100) return 'under';
    if (actual > target + 100) return 'over';
    return 'full';
  }

  /** True when createdDate parses to a date more than 3 months before today. */
  private createdMoreThan3MonthsAgo(createdDate: string | undefined): boolean {
    if (!createdDate) return false;
    const created = new Date(createdDate);
    if (isNaN(created.getTime())) return false;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 3);
    return created < cutoff;
  }

  applyFilter(): void {
    const baseFiltered = this.invoices.filter(invoice => {
      if (this.campaignNameFilter && this.campaignNameFilter.trim()) {
        const campaignName = (this.campaignLookup[invoice.campaignId]?.name || '').toLowerCase();
        if (!campaignName.includes(this.campaignNameFilter.trim().toLowerCase())) return false;
      }

      if (this.rateFilter && this.rateFilter.trim()) {
        const filterValue = parseFloat(this.rateFilter.trim());
        if (isNaN(filterValue) || this.getInvoiceRate(invoice) !== filterValue) return false;
      }

      if (this.accountFilter && this.accountFilter.trim()) {
        const accountName = this.getAccountName(invoice).toLowerCase();
        if (!accountName.includes(this.accountFilter.trim().toLowerCase())) return false;
      }

      if (this.selectedCreatorName && this.selectedCreatorName.trim()) {
        const creatorName = this.getCreatorName(invoice).toLowerCase();
        if (!creatorName.includes(this.selectedCreatorName.trim().toLowerCase())) return false;
      }

      if (this.refFilterString && this.refFilterString.trim()) {
        const refLower = this.refFilterString.trim().toLowerCase();
        if (!invoice.inRef?.toLowerCase().includes(refLower) && !invoice.outRef?.toLowerCase().includes(refLower)) return false;
      }

      return true;
    });

    // Summary boxes always reflect the text-filtered set, ignoring the status box filter
    this.calculateSummaryNumbers(baseFiltered);

    // Status box filter applied on top for the displayed list
    this.filteredInvoices = this.activeStatusFilter
      ? baseFiltered.filter(i => this.getInvoiceStatusKey(i) === this.activeStatusFilter)
      : baseFiltered;

    const sortFn = (a: Invoice, b: Invoice) => {
      const aDate = a.issueDate ?? '0000-01-01';
      const bDate = b.issueDate ?? '0000-01-01';
      return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
    };
    this.filteredInvoices.sort(sortFn);
  }

  private calculateSummaryNumbers(invoices: Invoice[]): void {
    this.completedInvoiceAmount = 0;
    this.noInvoiceAmount = 0;
    this.invoicedNotCollected = 0;
    this.overdueInvoiceAmount = 0;
    this.creatorPaymentNeeded = 0;

    invoices.forEach(i => {
      // Coerce defensively: a single string amount would turn every running total from
      // that point on into a concatenated string, which the currency pipe then rejects.
      const actualPay = toInvoiceAmount(i.actualPayAmount) ?? 0;

      this.completedInvoiceAmount += actualPay;

      if (i.invoiceNumber === 'no-invoice') {
        this.noInvoiceAmount += toInvoiceAmount(this.campaignLookup[i.campaignId]?.rate) ?? 0;
      }

      const invoiceRate = this.getInvoiceRate(i);

      if (invoiceRate > 0 && actualPay === 0) {
        this.invoicedNotCollected += invoiceRate;
      }

      if (this.isOverdue(i.dueDate!) && !i.paidDate) {
        this.overdueInvoiceAmount += invoiceRate;
      }

      // Set creatorPaymentNeeded based on: i.actualPayAmount && i.actualPayAmount > 0 && !i.creatorPaidDate
      if (actualPay > 0 && !i.creatorPaidDate) {
        this.creatorPaymentNeeded += actualPay * 0.9;
      }
    });
  }

  isOverdue(dueDate: string): boolean
  {
    const today = new Date().toISOString().split('T')[0]
    return dueDate < today;
  }

  // getPaidInvoiceCount(campaign: Campaign): number
  // {
  //   let count = 0;
  //   for (let i = 0; i < this.invoices.length; i++) {
  //     if (this.invoices[i].paidDate) {
  //       count++;
  //     }
  //   }
  //   return count;
  // }

  /**
   * True only when the invoice has at least one line item and every line item has a
   * lineItemType set. Drives the "E" column checkmark; placeholders (no line items) are false.
   */
  allLineItemsHaveType(invoice: Invoice): boolean {
    if (!invoice.lineItems || invoice.lineItems.length === 0) return false;
    return invoice.lineItems.every(li => !!li.lineItemType);
  }

  getInvoiceRate(invoice: Invoice): number
  {
    let amount = 0;

    for (let j = 0; j < invoice.lineItems.length; j++) {
      amount += toInvoiceAmount(invoice.lineItems[j].unitPrice) ?? 0;
    }

    return amount;
  }

  getCreatorName(invoice: Invoice): string {
    // First try to get the campaign for this invoice
    const campaign = this.campaigns.find(c => c.id === invoice.campaignId);
    if (campaign) {
      // If campaign has creatorId, look it up in roster
      if (campaign.creatorId && this.rosterLookup[campaign.creatorId]) {
        return this.rosterLookup[campaign.creatorId];
      }
      // Fallback to campaign's existing creatorName
      if (campaign.creatorName) {
        return campaign.creatorName;
      }
    }
    return '—'; // Return dash if no creator name found
  }

  getAccountName(invoice: Invoice): string {
    const accountId = this.campaignLookup[invoice.campaignId]?.accountId;
    if (!accountId) return '—';
    return this.accountLookup[accountId] || '—';
  }

  private calculateInvoiceTotal(invoice: Invoice): number {
    if (!invoice.lineItems || invoice.lineItems.length === 0) {
      return 0;
    }
    return invoice.lineItems.reduce((sum, item) => sum + (toInvoiceAmount(item.unitPrice) ?? 0), 0);
  }

  downloadCSV(): void {
    // Define CSV headers
    const headers = [
      'Invoice Number',
      'Campaign Name',
      'Creator Name',
      'Issue Date',
      'Due Date',
      'Paid Date',
      'Creator Paid Date',
      'Total Amount',
      'Actual Pay Amount',
      'Creator Pay Amount',
      'Status'
    ];

    // Convert filteredInvoices to CSV rows
    const rows = this.filteredInvoices.map(invoice => {
      const totalAmount = this.calculateInvoiceTotal(invoice);
      const campaignName = this.campaignLookup[invoice.campaignId]?.name || '—';
      const creatorName = this.getCreatorName(invoice);

      return [
        invoice.invoiceNumber || '—',
        campaignName,
        creatorName,
        invoice.issueDate || '—',
        invoice.dueDate || '—',
        invoice.paidDate || '—',
        invoice.creatorPaidDate || '—',
        totalAmount.toFixed(2),
        (toInvoiceAmount(invoice.actualPayAmount) ?? 0).toFixed(2),
        (toInvoiceAmount(invoice.creatorPayAmount) ?? 0).toFixed(2),
        invoice.statusNote || '—'
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `invoices_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // getCreatorPayAmount(campaign: Campaign): number
  // {
  //   let amount = 0;
  //   for (let i = 0; i < invoices.length; i++) {
  //     amount += invoices[i].creatorPayAmount ?? 0;
  //   }

  //   return amount;
  // }


}
